"use client";

import { Toast, ToastToggle } from "flowbite-react";
import React, { useEffect, useState } from "react";
import { HiCheck } from "react-icons/hi";
import { BiErrorCircle } from "react-icons/bi";
import AppSpinner from "../spinners/AppSpinner";

interface AppToastProps {
  setShow: (status: boolean) => void;
  show?: boolean;
  options: AppToastOptions;
}

const getStatusColor = (status: ToastStatus): string => {
  switch (status) {
    case "success":
      return "bg-green-100 text-green-500 dark:bg-green-800 dark:text-green-200";
    case "loading":
      return "bg-yellow-100 text-yellow-500 dark:bg-yellow-800 dark:text-yellow-200";
    case "failure":
      return "bg-red-100 text-red-500 dark:bg-red-800 dark:text-red-200";
    default:
      return "";
  }
};

const getStatusIcon = (status: ToastStatus): React.ReactNode => {
  switch (status) {
    case "success":
      return <HiCheck className="h-5 w-5" />;
    case "loading":
      return <AppSpinner />;
    case "failure":
      return <BiErrorCircle className="h-5 w-5" />;
    default:
      return null;
  }
};

const defaults: AppToastProps = {
  setShow: (status: boolean) => {},
  show: true,
  options: {
    message: "",
    dismissible: true,
  },
};

const AppToast: React.FC<AppToastProps> = ({
  setShow,
  show,
  options,
} = defaults) => {
  const { status, message, dismissible, duration } = options;

  useEffect(() => {
    if (duration) {
      const timer = setTimeout(() => {
        setShow(false);
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, setShow, options]);

  const onDismiss = () => {
    setShow(false);
  };

  return (
    <>
      {show && (
        <Toast className="fixed bottom-0 left-0 m-6">
          <div
            className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${getStatusColor(status)}`}
          >
            {getStatusIcon(status)}
          </div>
          <div className="ml-3 text-sm font-normal">{message}</div>
          {dismissible && <ToastToggle onDismiss={onDismiss} />}
        </Toast>
      )}
    </>
  );
};

export default AppToast;
